import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, getToken } = useAuth();

  const fetchWishlistItems = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken(false); // E-commerce token
      if (!token) return;

      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setWishlistItems(data.data);
      }
    } catch (error) {
      console.error('Error fetching wishlist items:', error);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlistItems();
    } else {
      setWishlistItems([]);
    }
  }, [fetchWishlistItems, isAuthenticated]);

  const addToWishlist = async (productId) => {
    try {
      const token = getToken(false);
      if (!token) {
        return { success: false, message: 'Please login to save items to your wishlist' };
      }

      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/wishlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId })
      });

      const data = await response.json();
      if (data.success) {
        await fetchWishlistItems();
        return { success: true };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { success: false, message: error.message };
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      const token = getToken(false);
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/wishlist/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setWishlistItems((prev) => prev.filter((item) => item.product_id !== productId));
        return { success: true };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { success: false, message: error.message };
    }
  };

  const wishlistProductIds = useMemo(
    () => new Set(wishlistItems.map((item) => item.product_id)),
    [wishlistItems]
  );

  const toggleWishlist = async (productId) => {
    if (wishlistProductIds.has(productId)) {
      return removeFromWishlist(productId);
    }
    return addToWishlist(productId);
  };

  const value = {
    wishlistItems,
    wishlistProductIds,
    loading,
    fetchWishlistItems,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    wishlistCount: wishlistItems.length
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
